/**
 * Lobby: assemble the loadout, then create a room or join one with a code.
 *
 * No accounts, no matchmaking queue — the code is the whole social layer.
 *
 * The loadout is decided *here*, before any room exists: a parry spell (built
 * in the tutorial) and an attack spell (built here). Both are authored offline
 * and stored in the browser, and both are re-priced by the server the moment
 * they are carried into a room, so nothing forged on this screen can buy an
 * advantage.
 */
import { useCallback, useState } from 'react';
import { PROJECTILE_SPEED_SCALE, isRoomCode, makeId } from '@spell/shared-types';
import type { Spell } from '@spell/spell-schema';
import { useFlow } from '../gameplay/flowStore';
import { useNet } from '../networking/netStore';
import { net } from '../networking/net';
import { sfx } from '../audio/sfx';
import { SpellCraft, type CraftResult } from '../crafting/SpellCraft';
import { buildSpellLocally } from '../crafting/buildSpell';
import { QUALITY, useSettings, type QualityPreset } from '../gameplay/settingsStore';

export interface LobbyProps {
  onConnected: () => void;
  onPractice: () => void;
}

/** Which slot the forge is currently open for, if any. */
type CraftTarget = 'parry' | 'attack';

const CRAFT_COPY: Record<
  CraftTarget,
  { title: string; defaults: { chant: string; description: string }; intro: React.ReactNode }
> = {
  parry: {
    title: '패링 주문 다시 만들기',
    defaults: { chant: 'Mirror Break', description: '공격을 산산조각 내고 일부를 상대에게 돌려보낸다.' },
    intro: (
      <p className="subtitle">
        모든 패링 주문은 타이밍만 맞으면 공격을 막습니다. 당신이 정하는 것은 <b>막은 다음에 일어날 일</b>입니다.
        <br />
        반사, 마나 흡수, 공격 저장, 순간이동, 상대 기절, 다음 주문 강화, 완전 제거 — 원하는 대로 적으세요.
      </p>
    ),
  },
  attack: {
    title: '공격 주문 만들기',
    defaults: { chant: 'Ember Lance', description: '앞으로 곧게 날아가 폭발하는 거대한 화염 창' },
    intro: (
      <p className="subtitle">
        대련장에 들고 들어갈 주문 하나를 미리 만들어 둡니다. 공격이 아니어도 됩니다 — 회복, 함정, 방어, 강화 무엇이든
        좋습니다.
        <br />
        확정하면 상대에게는 영창 이름과 대략적인 기능만 공개됩니다.
      </p>
    ),
  },
};

export function Lobby({ onConnected, onPractice }: LobbyProps) {
  const name = useFlow((s) => s.name);
  const setName = useFlow((s) => s.setName);
  const parrySpell = useFlow((s) => s.parrySpell);
  const attackSpell = useFlow((s) => s.attackSpell);
  const setParrySpell = useFlow((s) => s.setParrySpell);
  const setAttackSpell = useFlow((s) => s.setAttackSpell);
  const resetProfile = useFlow((s) => s.resetProfile);
  const error = useFlow((s) => s.error);
  const setError = useFlow((s) => s.setError);
  const roomCode = useNet((s) => s.roomCode);
  const status = useNet((s) => s.status);

  const preset = useSettings((s) => s.preset);
  const setPreset = useSettings((s) => s.setPreset);

  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [crafting, setCrafting] = useState<CraftTarget | null>(null);
  const [building, setBuilding] = useState(false);
  /**
   * The speed rule the room will be opened under. Only the host picks it —
   * joining a room means accepting whatever it was opened with — so it lives
   * here rather than in stored settings.
   */
  const [speedScale, setSpeedScale] = useState<number>(PROJECTILE_SPEED_SCALE.default);

  /** Both spells are needed to face another duellist. */
  const loadoutReady = Boolean(parrySpell && attackSpell);

  const craft = useCallback(
    async (target: CraftTarget, result: CraftResult) => {
      setBuilding(true);
      setError(null);
      try {
        const built = await buildSpellLocally({
          ...result,
          type: target === 'parry' ? 'parry' : 'signature',
          ownerId: 'local',
          id: makeId(target === 'parry' ? 'parry' : 'sig'),
        });
        if (target === 'parry') setParrySpell(built.spell);
        else setAttackSpell(built.spell);
        setCrafting(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : '주문을 만들지 못했습니다.');
      } finally {
        setBuilding(false);
      }
    },
    [setAttackSpell, setError, setParrySpell],
  );

  /** Connect, hand over the loadout, and let the app switch screens. */
  const enter = useCallback(
    async (open: () => Promise<string>, next: () => void) => {
      setBusy(true);
      setError(null);
      sfx.unlock();
      try {
        useNet.getState().set({ status: 'connecting' });
        const joined = await open();
        useNet.getState().set({
          status: 'connected',
          roomCode: joined,
          sessionId: net.sessionId,
        });
        net.identify(name, parrySpell, attackSpell);
        next();
      } catch (err) {
        useNet.getState().set({ status: 'error' });
        setError(err instanceof Error ? err.message : '입장하지 못했습니다.');
      } finally {
        setBusy(false);
      }
    },
    [attackSpell, name, parrySpell, setError],
  );

  const create = () => void enter(() => net.createRoom(name, speedScale), onConnected);

  const join = () => {
    const normalized = code.toUpperCase();
    if (!isRoomCode(normalized)) {
      setError('초대 코드는 6자리입니다.');
      return;
    }
    void enter(async () => {
      await net.joinRoom(normalized, name);
      return normalized;
    }, onConnected);
  };

  const practice = () => void enter(() => net.createPractice(name, speedScale), onPractice);

  /* --- the forge, opened over the lobby ------------------------------- */
  if (crafting) {
    const copy = CRAFT_COPY[crafting];
    return (
      <div className="screen">
        {/* Matches `.craft`'s own width so the escape hatch lines up under it. */}
        <div style={{ width: 'min(940px, 96vw)' }}>
          <SpellCraft
            type={crafting === 'parry' ? 'parry' : 'signature'}
            title={copy.title}
            intro={copy.intro}
            defaults={copy.defaults}
            submitting={building}
            onSubmit={(result) => void craft(crafting, result)}
          />
          {error ? <div className="error">{error}</div> : null}
          <div className="row" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn btn--ghost"
              data-testid="craft-cancel"
              disabled={building}
              onClick={() => {
                setError(null);
                setCrafting(null);
              }}
            >
              취소하고 로비로
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <div className="panel lobby" data-testid="lobby">
        <h1 className="title">Spell</h1>
        <p className="subtitle">
          1대1 마법 대련. 최대 3라운드, 2선승제. 라운드 사이마다 새로운 주문을 하나씩 만듭니다.
        </p>

        <div className="section-label">듀얼리스트</div>
        <div className="row">
          <input
            className="field"
            data-testid="player-name"
            value={name}
            maxLength={20}
            onChange={(e) => setName(e.target.value)}
            placeholder="이름"
          />
          <button type="button" className="btn btn--ghost" onClick={() => setShowSettings((v) => !v)}>
            ⚙ 설정
          </button>
        </div>

        <div className="section-label">주문 준비</div>
        <div className="loadout">
          <LoadoutCard
            kind="parry"
            testId="loadout-parry"
            label="패링 주문"
            note="공격을 막고, 막은 다음에 일어날 일을 정합니다."
            spell={parrySpell}
            actionLabel={parrySpell ? '다시 만들기' : '만들기'}
            onCraft={() => setCrafting('parry')}
          />
          <LoadoutCard
            kind="attack"
            testId="loadout-attack"
            label="공격 주문"
            note="대련장에 들고 들어갈 시그니처 주문 하나."
            spell={attackSpell}
            actionLabel={attackSpell ? '다시 만들기' : '만들기'}
            onCraft={() => setCrafting('attack')}
          />
        </div>
        <div className="hint" style={{ marginTop: 8 }}>
          {loadoutReady
            ? '기본 공격은 없습니다 — 이 두 주문이 당신이 가진 전부입니다. 라운드 사이에 하나씩 더 만들 수 있습니다.'
            : '대련장에 들어가려면 패링 주문 1개와 공격 주문 1개가 필요합니다.'}
        </div>

        {showSettings ? (
          <>
            <div className="section-label">그래픽 품질</div>
            <div className="row row--wrap">
              {(Object.keys(QUALITY) as QualityPreset[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`btn ${preset === p ? 'btn--primary' : ''}`}
                  onClick={() => setPreset(p)}
                >
                  {p === 'low' ? '낮음' : p === 'medium' ? '보통' : '높음'}
                </button>
              ))}
              <span className="hint">
                전투 판정에 필요한 투사체와 경고 표시는 품질과 무관하게 동일하게 보입니다.
              </span>
            </div>
          </>
        ) : null}

        <div className="section-label">연습장</div>
        <div className="row row--wrap">
          <button
            type="button"
            className="btn"
            data-testid="enter-practice"
            disabled={busy || !parrySpell}
            onClick={practice}
          >
            {busy && status === 'connecting' ? '연결 중…' : '혼자 연습하기'}
          </button>
          <span className="hint">
            훈련 인형을 상대로 시간 제한 없이 연습합니다. 판정·피해량·패링 타이밍은 실제 대련과 완전히 같습니다.
          </span>
        </div>

        <div className="section-label">탄속</div>
        <div className="row">
          <input
            type="range"
            className="speed-slider"
            data-testid="speed-scale"
            min={PROJECTILE_SPEED_SCALE.min}
            max={PROJECTILE_SPEED_SCALE.max}
            step={0.05}
            value={speedScale}
            onChange={(e) => setSpeedScale(Number(e.target.value))}
          />
          <span className="speed-slider__value">{speedScale.toFixed(2)}×</span>
        </div>
        <div className="hint" style={{ marginTop: 6 }}>
          이 방의 모든 투사체가 얼마나 빠르게 날아갈지 — 주문마다 정해진 속도에 곱해집니다. 방을 열 때 정해지고 상대에게도
          공개되며, <b>입장 후에는 바꿀 수 없습니다.</b>
          <br />
          {speedScale < 0.9
            ? '느린 방입니다. 공격이 다가오는 시간이 길어서 패링을 배우기 좋습니다.'
            : speedScale > 1.1
              ? '빠른 방입니다. 읽을 시간이 짧아집니다.'
              : '기본 속도입니다.'}{' '}
          패링 판정 구간 자체는 어느 배율에서도 똑같습니다.
        </div>

        <div className="section-label">방 만들기</div>
        <div className="row">
          <button
            type="button"
            className="btn btn--primary"
            data-testid="create-room"
            disabled={busy || !loadoutReady}
            onClick={create}
          >
            {busy && status === 'connecting' ? '연결 중…' : '새 대련장 열기'}
          </button>
          {roomCode ? <div className="code-display">{roomCode}</div> : null}
        </div>

        <div className="section-label">코드로 참가</div>
        <div className="row">
          <input
            className="field field--code"
            data-testid="join-code"
            value={code}
            maxLength={6}
            placeholder="ABC123"
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') join();
            }}
          />
          <button
            type="button"
            className="btn"
            data-testid="join-room"
            disabled={busy || code.length !== 6 || !loadoutReady}
            onClick={join}
          >
            입장
          </button>
        </div>

        {error ? (
          <div className="error" data-testid="lobby-error">
            {error}
          </div>
        ) : null}

        <div className="section-label">기타</div>
        <div className="row row--wrap">
          <button type="button" className="btn btn--ghost" onClick={resetProfile}>
            튜토리얼과 주문 초기화
          </button>
          <span className="hint">회원가입은 없습니다. 진행 상황은 이 브라우저에만 저장됩니다.</span>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

interface LoadoutCardProps {
  kind: CraftTarget;
  testId: string;
  label: string;
  note: string;
  spell: Spell | null;
  actionLabel: string;
  onCraft: () => void;
}

/**
 * One loadout slot. Readiness is never signalled by colour alone: the slot
 * carries a glyph, a filled/empty state word and the spell's own chant.
 */
function LoadoutCard({ kind, testId, label, note, spell, actionLabel, onCraft }: LoadoutCardProps) {
  const accent = spell?.visuals.color ?? 'var(--ink-faint)';
  return (
    <div
      className={`loadout-slot ${spell ? 'loadout-slot--filled' : ''}`}
      data-testid={testId}
      data-filled={spell ? 'true' : 'false'}
    >
      <div className="loadout-slot__head">
        <span className="loadout-slot__glyph" style={{ color: accent }} aria-hidden>
          {kind === 'parry' ? '❖' : '✦'}
        </span>
        <span className="loadout-slot__label">{label}</span>
        <span className="loadout-slot__state">{spell ? '✓ 준비됨' : '· 비어 있음'}</span>
      </div>
      {spell ? (
        <>
          <div className="loadout-slot__chant" style={{ color: accent }}>
            {spell.chant}
          </div>
          <div className="loadout-slot__summary">{spell.summary}</div>
          <div className="hint" style={{ marginTop: 6 }}>
            {spell.stats.damage > 0 ? `피해 ${spell.stats.damage} · ` : ''}
            마나 {spell.stats.manaCost} · 재사용 {(spell.stats.cooldownMs / 1000).toFixed(1)}s
          </div>
        </>
      ) : (
        <div className="loadout-slot__summary">{note}</div>
      )}
      <button
        type="button"
        className={`btn ${spell ? 'btn--ghost' : 'btn--primary'}`}
        style={{ marginTop: 10 }}
        data-testid={`${testId}-craft`}
        onClick={onCraft}
      >
        {actionLabel}
      </button>
    </div>
  );
}
